import { useState } from "react";
import type { ApiError } from "@/lib/api";
import { errorMessage } from "@/lib/api";
import type { PlayerGoal } from "@/lib/types";
import { CoachModal } from "@/features/coach/components/CoachModal";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/toast";
import { GoalCard } from "./GoalCard";
import { useMyGoalReflection } from "../hooks/useMyGoalReflection";

/**
 * Viewer goal-reflection modal (M7) — opened from a goal card's
 * "Add reflection" button on the `/feedback` Development tab.
 * Ports the legacy `openGoalReflectionModal`
 * (`js/coaching/goals.js`).
 *
 * Shows the goal card read-only (`actions={false}`) for context,
 * then a reflection textarea. Submit → `POST /api/my-feedback/
 * goals/{id}/reflection`; on success the modal closes and the
 * hook invalidates the feedback + development queries so the new
 * "Latest reflection" appears on the card.
 *
 * Reuses `CoachModal` as the portal shell — it's a generic
 * one-modal-at-a-time portal, not coach-specific.
 */

export type GoalReflectionModalProps = {
  goal: PlayerGoal;
  onClose: () => void;
  /** Called after a reflection saves (the host may refetch). */
  onSaved?: () => void;
};

const MAX_REFLECTION_LENGTH = 1000;

export function GoalReflectionModal({
  goal,
  onClose,
  onSaved,
}: GoalReflectionModalProps) {
  const toast = useToast();
  const [reflection, setReflection] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { submitReflection, isPending } = useMyGoalReflection();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = reflection.trim();
    if (!text) {
      setError("Add a reflection before saving.");
      return;
    }
    setError(null);
    try {
      await submitReflection(goal.id, text);
      toast.success("Reflection submitted");
      onSaved?.();
      onClose();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(
        typeof apiErr?.detail === "string"
          ? apiErr.detail
          : "Could not save your reflection.",
      );
      toast.error("Couldn't submit reflection", {
        description: errorMessage(err),
      });
    }
  };

  return (
    <CoachModal
      title="Reflect on goal"
      onClose={onClose}
      data-testid="goal-reflection-modal"
    >
      <div className="flex flex-col gap-3">
        {/* The goal, read-only, for context. */}
        <GoalCard goal={goal} viewer actions={false} />

        <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
              Your reflection
            </span>
            <textarea
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              maxLength={MAX_REFLECTION_LENGTH}
              rows={4}
              autoFocus
              data-testid="goal-reflection-text"
              placeholder="What did you try? What should your coach know?"
              className="w-full bg-canvas border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>

          {error ? (
            <p
              className="text-destructive font-mono text-xs"
              data-testid="goal-reflection-error"
            >
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            disabled={isPending}
            data-testid="goal-reflection-submit"
          >
            {isPending ? "Saving…" : "Save reflection"}
          </Button>
        </form>
      </div>
    </CoachModal>
  );
}
